import { LocationPersistenceService } from '../../services/location/location-persistence.js'
import { cotrexSearchService } from '../../services/cotrex/cotrex-search-service.js'
import type { CotrexSearchResult } from '../../services/cotrex/cotrex-search-service.js'
import { openBetaSearchService } from '../../services/openbeta/openbeta-search-service.js'
import type { OpenBetaSearchResult } from '../../services/openbeta/openbeta-search-service.js'
import { logger } from '../../utils/logger.js'
import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { convertDistance } from '../../utils/units.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { formatPosition } from '../../lib/coordinates.js'
import {
  parseCoordinates,
  createPointBounds,
  calculateDistance,
} from '../../lib/coordinate-parser.js'
import { sortByRelevance } from '../../lib/relevance-scorer.js'
import type { CoordinateParseResult } from '../../lib/coordinate-parser.js'

// Material Web Components
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/icon/icon.js'
import '@material/web/menu/menu.js'
import '@material/web/menu/menu-item.js'
import '@material/web/progress/circular-progress.js'

const log = logger.create('search')

export type { CoordinateParseResult }

export interface LocationResult {
  displayName: string
  coordinates: [number, number] // [lat, lng]
  bounds?: {
    ne: [number, number]
    sw: [number, number]
  }
  type: string
  osmType?: 'N' | 'W' | 'R'
  osmId?: number
  osmValue?: string
  icon: string
  distance?: number
  source?: 'photon' | 'cotrex' | 'openbeta'
  cotrexMeta?: { manager: string; allowedUses: string; trailNum: string; placeId: number }
}

const ICON_BY_VALUE: Partial<Record<string, string>> = {
  path: 'directions_walk',
  footway: 'directions_walk',
  track: 'directions_walk',
  shelter: 'night_shelter',
  parking: 'local_parking',
  drinking_water: 'water_drop',
  camp_site: 'camping',
  alpine_hut: 'night_shelter',
  wilderness_hut: 'night_shelter',
  viewpoint: 'visibility',
}

const ICON_BY_KEY: Partial<Record<string, string>> = {
  natural: 'landscape',
  waterway: 'waves',
  highway: 'road',
  amenity: 'pin_drop',
  tourism: 'tour',
  place: 'location_city',
  leisure: 'park',
}

function getLocationIcon(osmKey: string, osmValue: string): string {
  return ICON_BY_VALUE[osmValue] ?? ICON_BY_KEY[osmKey] ?? 'location_on'
}

export interface LocationSearchOptions {
  biasPoint?: [number, number] // [lat, lng] to bias results towards
  maxResults?: number
  includeCoordinateParsing?: boolean
  minQueryLength?: number
}

@customElement('location-search')
export class LocationSearch extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
    }

    .search-container {
      position: relative;
    }

    .search-field {
      width: 100%;
      --md-outlined-text-field-container-shape: var(--location-search-border-radius, 12px);
    }

    .results-container {
      position: absolute;
      bottom: 100%;
      left: 0;
      right: 0;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      box-shadow: var(--cairn-shadow, 0 -2px 4px rgba(0, 0, 0, 0.1));
      max-height: 300px;
      overflow-y: auto;
      z-index: 5000;
      display: none;
    }

    /* Auto-detect direction based on available space */
    @supports (position: absolute) {
      .results-container {
        /* Default: dropdown goes up from bottom */
        bottom: 100%;
        top: auto;
      }

      /* If positioned near bottom of viewport, flip to go down */
      .search-container[data-position='bottom'] .results-container {
        bottom: auto;
        top: 100%;
        box-shadow: var(--cairn-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
      }
    }

    .results-container.open {
      display: block;
    }

    .result-item {
      padding: 12px var(--cairn-spacing-md, 16px);
      cursor: pointer;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      transition: background-color var(--cairn-animation-duration, 0.2s);
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .result-item:last-child {
      border-bottom: none;
    }

    .result-item:hover,
    .result-item.focused {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .result-icon {
      flex-shrink: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 20px !important;
    }

    .result-content {
      flex: 1;
      min-width: 0;
    }

    .result-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
    }

    .result-name {
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
      margin-bottom: var(--cairn-spacing-xs, 4px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .result-distance {
      flex-shrink: 0;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: nowrap;
    }

    .result-details {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--cairn-spacing-md, 16px);
    }

    .no-results {
      padding: var(--cairn-spacing-md, 16px);
      text-align: center;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .coordinate-hint {
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-background-color, #f8fafc);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .coordinate-detected {
      background: var(--cairn-success-background, #f0f9ff);
      color: var(--cairn-success-color, #059669);
    }

    .search-icon {
      cursor: pointer;
      color: var(--cairn-text-secondary-color, #64748b);
      transition: color 0.2s ease;
      font-size: 20px !important;
    }

    .search-icon:hover {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .search-icon:active {
      color: var(--cairn-primary-color-dark, #2563eb);
    }
  `

  @property({ type: String })
  value = ''

  @property({ type: String })
  label = 'Location'

  @property({ type: String })
  placeholder = 'Search for a city, peak, or coordinates...'

  @property({ type: Object })
  options: LocationSearchOptions = {
    maxResults: 8,
    includeCoordinateParsing: true,
    minQueryLength: 2,
  }

  @property({ type: String })
  direction: 'auto' | 'up' | 'down' = 'auto'

  @property({ type: Array })
  biasPoint?: [number, number]

  @state()
  private searchResults: LocationResult[] = []

  @state()
  private loading = false

  @state()
  private showResults = false

  @state()
  private coordinateInfo: CoordinateParseResult | null = null

  @state()
  private focusedIndex = -1

  private abortController: AbortController | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private readonly DEBOUNCE_MS = 300
  private readonly MIN_TYPEAHEAD_LENGTH = 3
  private settingsStorage = new SettingsStorage()

  private async loadMaterialIcons(): Promise<void> {
    try {
      const materialStyle = document.createElement('style')
      materialStyle.textContent = `
        @font-face {
          font-family: 'Material Symbols Outlined';
          font-style: normal;
          font-weight: 400;
          src: url('/node_modules/@material-symbols/font-400/material-symbols-outlined.woff2') format('woff2');
        }
        .material-icons {
          font-family: 'Material Symbols Outlined';
          font-weight: normal;
          font-style: normal;
          font-size: 24px;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-feature-settings: 'liga';
          -webkit-font-smoothing: antialiased;
        }
      `
      this.shadowRoot?.appendChild(materialStyle)
    } catch (error) {
      log.error('Failed to load Material Symbols CSS:', error)
    }
  }

  override connectedCallback(): void {
    super.connectedCallback()
    void this.loadMaterialIcons()
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  private getMapCenterBias(): [number, number] | undefined {
    const loc = LocationPersistenceService.getLastLocation()
    if (!loc) return undefined
    return [loc.lat, loc.lng]
  }

  private getGpsPosition(): [number, number] | undefined {
    const loc = LocationPersistenceService.getLastLocation()
    if (loc?.source !== 'gps') return undefined
    return [loc.lat, loc.lng]
  }

  private handleInput(e: Event): void {
    const target = e.target as HTMLInputElement
    const newValue = target.value
    this.value = newValue

    this.coordinateInfo = null

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (!newValue.trim() || newValue.length < (this.options.minQueryLength ?? 2)) {
      this.hideResults()
      return
    }

    if (this.options.includeCoordinateParsing) {
      const coordResult = parseCoordinates(newValue)
      if (coordResult) {
        this.coordinateInfo = coordResult
        this.showCoordinateResult()
        return
      }
    }

    if (newValue.trim().length >= this.MIN_TYPEAHEAD_LENGTH) {
      this.debounceTimer = setTimeout(() => {
        void this.searchLocation(newValue.trim())
      }, this.DEBOUNCE_MS)
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.showResults) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        void this.performSearch()
      }
      return
    }

    const items = this.getNavigableItems()
    const count = items.length

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.focusedIndex = this.focusedIndex < count - 1 ? this.focusedIndex + 1 : 0
        this.scrollFocusedIntoView()
        break
      case 'ArrowUp':
        e.preventDefault()
        this.focusedIndex = this.focusedIndex > 0 ? this.focusedIndex - 1 : count - 1
        this.scrollFocusedIntoView()
        break
      case 'Enter':
        e.preventDefault()
        if (this.focusedIndex >= 0 && this.focusedIndex < count) {
          this.selectResult(items[this.focusedIndex])
        } else {
          if (this.debounceTimer) clearTimeout(this.debounceTimer)
          void this.performSearch()
        }
        break
      case 'Escape':
        e.preventDefault()
        this.hideResults()
        break
    }
  }

  private getNavigableItems(): (LocationResult | CoordinateParseResult)[] {
    const items: (LocationResult | CoordinateParseResult)[] = []
    if (this.coordinateInfo) items.push(this.coordinateInfo)
    items.push(...this.searchResults)
    return items
  }

  private scrollFocusedIntoView(): void {
    requestAnimationFrame(() => {
      const container = this.shadowRoot?.querySelector('.results-container')
      const items = container?.querySelectorAll('.result-item')
      if (items && this.focusedIndex >= 0 && this.focusedIndex < items.length) {
        items[this.focusedIndex].scrollIntoView({ block: 'nearest' })
      }
    })
  }

  private async performSearch(): Promise<void> {
    const query = this.value.trim()
    if (!query || query.length < (this.options.minQueryLength ?? 2)) {
      return
    }

    if (this.options.includeCoordinateParsing) {
      const coordResult = parseCoordinates(query)
      if (coordResult) {
        this.coordinateInfo = coordResult
        this.showCoordinateResult()
        return
      }
    }

    void this.searchLocation(query)
  }

  private showCoordinateResult(): void {
    this.searchResults = []
    this.detectDropdownDirection()
    this.showResults = true
  }

  private async searchLocation(query: string): Promise<void> {
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()
    this.loading = true
    this.showResults = true
    const signal = this.abortController.signal

    let photonResults: LocationResult[] = []
    let cotrexResults: LocationResult[] = []
    let openBetaResults: LocationResult[] = []

    const mergeAndUpdate = (): void => {
      if (signal.aborted) return
      const photonRankMap = new Map(photonResults.map((r, i) => [r, i] as const))
      const merged = [...cotrexResults, ...openBetaResults, ...photonResults]
      this.searchResults = sortByRelevance(merged, query, photonRankMap, photonResults.length)
      this.focusedIndex = -1
      requestAnimationFrame((): void => this.detectDropdownDirection())
    }

    try {
      // All local sources fire in parallel — no timeouts needed
      await Promise.all([
        this.searchPhoton(query).then(r => {
          photonResults = r
          mergeAndUpdate()
        }),
        this.searchCotrex(query).then(r => {
          cotrexResults = r
          mergeAndUpdate()
        }),
        this.searchOpenBeta(query).then(r => {
          openBetaResults = r
          mergeAndUpdate()
        }),
      ])
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      log.error('Location search error:', error)
      this.searchResults = []
    } finally {
      this.loading = false
      requestAnimationFrame((): void => this.detectDropdownDirection())
    }
  }

  private async searchPhoton(query: string): Promise<LocationResult[]> {
    const url = new URL('https://photon.komoot.io/api/')
    url.searchParams.set('q', query)
    url.searchParams.set('limit', (this.options.maxResults ?? 8).toString())
    url.searchParams.set('lang', 'en')

    const biasPoint = this.biasPoint ?? this.options.biasPoint ?? this.getMapCenterBias()
    if (biasPoint) {
      url.searchParams.set('lat', biasPoint[0].toString())
      url.searchParams.set('lon', biasPoint[1].toString())
    }

    const response = await fetch(url.toString(), {
      signal: this.abortController!.signal,
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()

    interface PhotonFeature {
      geometry: { coordinates: [number, number] }
      properties: {
        name?: string
        city?: string
        state?: string
        country?: string
        osm_type?: 'N' | 'W' | 'R'
        osm_id?: number
        osm_key?: string
        osm_value?: string
        extent?: [number, number, number, number]
      }
    }

    return ((data as { features?: PhotonFeature[] }).features ?? [])
      .map((feature: PhotonFeature): LocationResult => {
        const coords = feature.geometry.coordinates
        const props = feature.properties

        let displayName = props.name ?? ''
        if (props.city && props.city !== props.name) {
          displayName += `, ${props.city}`
        }
        if (props.state) {
          displayName += `, ${props.state}`
        }
        if (props.country) {
          displayName += `, ${props.country}`
        }

        let bounds: { sw: [number, number]; ne: [number, number] }
        if (props.extent) {
          bounds = {
            sw: [props.extent[1], props.extent[0]] as [number, number],
            ne: [props.extent[3], props.extent[2]] as [number, number],
          }
        } else {
          bounds = createPointBounds([coords[1], coords[0]], 1)
        }

        const resultCoords: [number, number] = [coords[1], coords[0]]

        let distance: number | undefined
        const gpsPos = this.getGpsPosition()
        if (gpsPos) {
          distance = calculateDistance(gpsPos, resultCoords)
        }

        return {
          displayName: displayName || 'Unknown location',
          coordinates: resultCoords,
          bounds,
          type: props.osm_key ?? 'location',
          ...(props.osm_type ? { osmType: props.osm_type } : {}),
          ...(props.osm_id != null ? { osmId: props.osm_id } : {}),
          ...(props.osm_value ? { osmValue: props.osm_value } : {}),
          icon: getLocationIcon(props.osm_key ?? '', props.osm_value ?? ''),
          ...(distance != null ? { distance } : {}),
          source: 'photon',
        }
      })
      .sort((a, b): number => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance
        }
        return 0
      })
  }

  private async searchCotrex(query: string): Promise<LocationResult[]> {
    try {
      await cotrexSearchService.ensureLoaded()
      const results: CotrexSearchResult[] = cotrexSearchService.search(query, 3)
      const biasPoint = this.biasPoint ?? this.options.biasPoint ?? this.getMapCenterBias()
      return results.map((r): LocationResult => {
        const coords: [number, number] = [r.entry.c[0], r.entry.c[1]]
        let distance: number | undefined
        if (biasPoint) distance = calculateDistance(biasPoint, coords)
        return {
          displayName: r.entry.n,
          coordinates: coords,
          bounds: {
            sw: [r.entry.b[1], r.entry.b[0]],
            ne: [r.entry.b[3], r.entry.b[2]],
          },
          type: 'trail',
          icon: 'hiking',
          source: 'cotrex',
          ...(distance != null ? { distance } : {}),
          cotrexMeta: {
            manager: r.entry.m,
            allowedUses: r.entry.u,
            trailNum: r.entry.t,
            placeId: r.entry.p ?? 0,
          },
        }
      })
    } catch (err) {
      log.warn('COTREX search failed:', err)
      return []
    }
  }

  private async searchOpenBeta(query: string): Promise<LocationResult[]> {
    try {
      await openBetaSearchService.ensureLoaded()
      const biasPoint = this.biasPoint ?? this.options.biasPoint ?? this.getMapCenterBias()
      const results: OpenBetaSearchResult[] = openBetaSearchService.search(query, biasPoint, 5)
      return results.map((r): LocationResult => {
        if (r.kind === 'area') {
          const path = r.pathTokens.length > 1 ? r.pathTokens.slice(0, -1).join(' > ') : ''
          let distance: number | undefined
          if (biasPoint) distance = calculateDistance(biasPoint, r.coordinates)
          return {
            displayName: r.name,
            coordinates: r.coordinates,
            bounds: createPointBounds(r.coordinates, 2),
            type: 'climbing',
            icon: 'terrain',
            source: 'openbeta',
            ...(distance != null ? { distance } : {}),
            ...(path
              ? {
                  cotrexMeta: {
                    manager: path,
                    allowedUses: `${r.totalClimbs} climbs`,
                    trailNum: '',
                    placeId: 0,
                  },
                }
              : {}),
          }
        }
        // Climb result
        const path = r.areaPathTokens.length > 0 ? r.areaPathTokens.join(' > ') : r.areaName
        let distance: number | undefined
        if (biasPoint) distance = calculateDistance(biasPoint, r.areaCoordinates)
        const typeLabel = r.grade ? `${r.grade} ` : ''
        return {
          displayName: r.name,
          coordinates: r.areaCoordinates,
          bounds: createPointBounds(r.areaCoordinates, 2),
          type: 'climbing',
          icon: 'straighten',
          source: 'openbeta',
          ...(distance != null ? { distance } : {}),
          cotrexMeta: {
            manager: path,
            allowedUses: `${typeLabel}climb`,
            trailNum: '',
            placeId: 0,
          },
        }
      })
    } catch (err) {
      log.warn('OpenBeta search failed:', err)
      return []
    }
  }

  private selectResult(result: LocationResult | CoordinateParseResult): void {
    if ('displayName' in result) {
      // Location result
      this.value = result.displayName
      this.dispatchEvent(
        new CustomEvent('location-selected', {
          detail: {
            location: result.displayName,
            coordinates: result.coordinates,
            bounds: result.bounds,
            osmType: result.osmType,
            osmId: result.osmId,
            icon: result.icon,
            source: result.source,
          },
        })
      )
    } else {
      // Coordinate result
      const coordString =
        result.format === 'decimal'
          ? this.formatCoords(result.coordinates[0], result.coordinates[1])
          : this.value

      this.dispatchEvent(
        new CustomEvent('location-selected', {
          detail: {
            location: coordString,
            coordinates: result.coordinates,
            bounds: result.bounds,
          },
        })
      )
    }

    this.hideResults()
  }

  private hideResults(): void {
    this.showResults = false
    this.searchResults = []
    this.focusedIndex = -1
  }

  private clearInput = (): void => {
    this.value = ''
    this.coordinateInfo = null
    this.hideResults()
  }

  private detectDropdownDirection(): void {
    const searchContainer = this.shadowRoot?.querySelector('.search-container') as
      | HTMLElement
      | undefined
    if (!searchContainer) {
      return
    }

    if (this.direction === 'down') {
      searchContainer.setAttribute('data-position', 'bottom')
      return
    }
    if (this.direction === 'up') {
      searchContainer.removeAttribute('data-position')
      return
    }

    const rect = searchContainer.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceAbove = rect.top
    const spaceBelow = viewportHeight - rect.bottom

    if (spaceBelow > spaceAbove && spaceBelow > 200) {
      searchContainer.setAttribute('data-position', 'bottom')
    } else {
      searchContainer.removeAttribute('data-position')
    }
  }

  private handleFocus(): void {
    if (this.value.trim() && (this.searchResults.length > 0 || this.coordinateInfo)) {
      this.showResults = true
    }
  }

  private handleBlur(): void {
    // Delay hiding to allow click events on results
    setTimeout((): void => this.hideResults(), 150)
  }

  /**
   * Format distance according to user's unit preferences
   */
  private formatDistance(distanceKm: number): string {
    const settings = this.settingsStorage.getSettingsWithDefaults()
    const meters = distanceKm * 1000
    return convertDistance(meters, settings.unitSystem)
  }

  private formatCoords(lat: number, lng: number): string {
    const settings = this.settingsStorage.getSettingsWithDefaults()
    return formatPosition({ lat, lng }, settings.coordinateFormat, settings.coordinatePrecision)
  }

  override render(): unknown {
    return html`
      <div class="search-container">
        <md-outlined-text-field
          class="search-field"
          .label=${this.label}
          .value=${this.value}
          .placeholder=${this.placeholder}
          @input=${this.handleInput}
          @keydown=${this.handleKeyDown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
        >
          <md-icon-button
            slot="trailing-icon"
            @click=${this.value.trim() ? this.clearInput : this.performSearch}
            ?disabled=${this.loading}
            aria-label=${this.value.trim() ? 'Clear' : 'Search'}
            title=${this.value.trim() ? 'Clear' : 'Search'}
          >
            <span class="material-icons">
              ${this.loading ? 'hourglass_empty' : this.value.trim() ? 'close' : 'search'}
            </span>
          </md-icon-button>
        </md-outlined-text-field>

        <div
          class="results-container ${this.showResults ? 'open' : ''}"
          part="results-container"
          role="listbox"
        >
          ${this.coordinateInfo
            ? html`
                <div class="coordinate-hint coordinate-detected">
                  📍 Coordinates detected: ${this.coordinateInfo.format.toUpperCase()} format
                </div>
                <div
                  class="result-item ${this.focusedIndex === 0 ? 'focused' : ''}"
                  role="option"
                  aria-selected=${this.focusedIndex === 0}
                  @click=${(): void => this.selectResult(this.coordinateInfo!)}
                >
                  <div class="result-name">
                    ${this.formatCoords(
                      this.coordinateInfo.coordinates[0],
                      this.coordinateInfo.coordinates[1]
                    )}
                  </div>
                  <div class="result-details">
                    ${this.coordinateInfo.format.toUpperCase()} coordinates
                  </div>
                </div>
              `
            : ''}
          ${this.loading
            ? html`
                <div class="loading">
                  <md-circular-progress indeterminate></md-circular-progress>
                </div>
              `
            : ''}
          ${!this.loading &&
          !this.coordinateInfo &&
          this.searchResults.length === 0 &&
          this.showResults
            ? html` <div class="no-results">No locations found</div> `
            : ''}
          ${this.searchResults.map((result, i): unknown => {
            const idx = this.coordinateInfo ? i + 1 : i
            return html`
              <div
                class="result-item ${this.focusedIndex === idx ? 'focused' : ''}"
                role="option"
                aria-selected=${this.focusedIndex === idx}
                @click=${(): void => this.selectResult(result)}
              >
                <span class="material-icons result-icon">${result.icon}</span>
                <div class="result-content">
                  <div class="result-header">
                    <span class="result-name">${result.displayName.split(',')[0]}</span>
                    ${result.distance !== undefined
                      ? html`<span class="result-distance"
                          >${this.formatDistance(result.distance)}</span
                        >`
                      : ''}
                  </div>
                  <div class="result-details">
                    ${result.source === 'openbeta' && result.cotrexMeta
                      ? html`OpenBeta ·
                        ${result.cotrexMeta.allowedUses}${result.cotrexMeta.manager
                          ? html` · ${result.cotrexMeta.manager}`
                          : ''}`
                      : result.source === 'cotrex' && result.cotrexMeta
                        ? html`COTREX · ${result.cotrexMeta.manager} ·
                          ${result.cotrexMeta.allowedUses}`
                        : result.displayName}
                  </div>
                </div>
              </div>
            `
          })}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'location-search': LocationSearch
  }
}
